/**
 * Wave K — file format IO, audit/repair tools, data tables, parameters,
 * xref operations and automation commands. Import parsing reuses the
 * CadParserService (DXF/DWG) pattern: formats that cannot be decoded
 * locally report honestly instead of failing silently; export/audit/
 * parametrize operate on real ledger data. (docs/architecture/command-parity-plan.md)
 */

import { runInteraction, PT, PN, PTxt, PK, PLoop, PLoopK, requireSelection, entityToPolyline, makeEntity } from './lib';
import { undoableAdd, undoableDelete, undoableModify, undoableReplace } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useDocumentStore } from '../../../state/useDocumentStore';
import { useDynamicInputStore } from '../../../state/useDynamicInputStore';
import { parseScript, runScriptSteps, loadScriptSource } from '../../../core/script/scrRunner';
import { useBIMStore } from '../../../state/useBIMStore';
import { useMacroStore, useViewStore, useBlockStore } from '../../../state/commandAuxStores';
import { parseDocument } from '../../../utils/ProjectFileIO';
import { isFileSystemAccessSupported, pickFileToOpen, readHandleText, pickLocalFile } from '../../../services/fileSystem/fileAccess';
import { downloadTextFile } from '../../../services/fileSystem/fileAccess';
import { renderPdfPage } from '../../../services/cad/pdfRaster';
import { parsePointCloudFile } from '../../../services/pointcloud/PointCloudParser';
import { useSystemVarsStore } from '../../../state/useSystemVarsStore';
import { createRecoveryReport, summarizeRecoveryReport, writeRecoveryLog } from '../../../services/fileSystem/recoveryReport';
import { sha256FileHex } from '../../../services/fileSystem/integrity';
import { globalParameterManager } from '../../../core/services';
import { notify } from '../../../utils/notify';
import { parseCsv, normalizeTableCells, useDataLinkStore } from '../tableModel';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import i18n from '../../../i18n';
import { useAcadPgpStore } from '../../../state/useAcadPgpStore';
import { reapplyAcadPgp } from '../../../utils/acadPgp';
import { parseObjText } from '../../../services/obj/objParser';
import { v4 as uuidv4 } from 'uuid';

/** Open a file picker (native or in-app) and hand text to a callback. */
async function pickAndRead(label: string, onText: (name: string, text: string) => void): Promise<void> {
    if (isFileSystemAccessSupported()) {
        const handle = await pickFileToOpen();
        if (!handle) return;
        const text = await readHandleText(handle);
        onText(handle.name, text);
    }
    // Fallback: in-app browser (existing flow) — guide the user.
    window.dispatchEvent(new CustomEvent('yap:open-file-browser', { detail: { mode: 'open' } }));
}

export const fileRunners: RunnerMap = {
    'file.dgnImport': () => importFormat('DGNIMPORT', 'dgn'),
    'file.dgnAttach': () => importFormat('DGNATTACH', 'dgn'),
    'file.fbxImport': () => importFormat('FBXIMPORT', 'fbx'),
    'file.objImport': () => importFormat('OBJIMPORT', 'obj'),
    'file.markup': () => importFormat('MARKUP', 'dwf'),

    'file.recover': () => {
        const startedAt = Date.now();
        const doc = useDocumentStore.getState();
        const objs = Object.values(doc.sceneObjects);
        const bad = objs.filter((o) => !o.id || !o.source);
        if (bad.length === 0 && objs.length > 0) {
            void (async () => {
                // Faz 9.4: even a clean sweep writes a recovery report.
                const report = createRecoveryReport({
                    status: 'clean',
                    fileName: doc.displayName,
                    sizeBytes: objs.length,
                    sourceSha256: null,
                    entitiesScanned: objs.length,
                    entitiesRemoved: 0,
                    diagnostics: ['document structure healthy'],
                    startedAt,
                });
                await writeRecoveryLog(report);
                notify(summarizeRecoveryReport(report), 'info');
            })();
            return `RECOVER: document structure is healthy (${objs.length} objects).`;
        }
        if (objs.length === 0) return 'RECOVER: the current document is empty.';
        const cleaned = objs.filter((o) => o.id && o.source);
        doc.replaceSceneLedger(cleaned);
        void (async () => {
            const report = createRecoveryReport({
                status: 'recovered',
                fileName: doc.displayName,
                sizeBytes: objs.length,
                sourceSha256: null,
                entitiesScanned: objs.length,
                entitiesRemoved: bad.length,
                diagnostics: bad.map((o) => `dropped malformed record ${String(o.id ?? '(no id)')}`),
                startedAt,
            });
            await writeRecoveryLog(report);
            notify(summarizeRecoveryReport(report), 'warning');
        })();
        return `RECOVER: ${bad.length} malformed record(s) dropped; ${cleaned.length} kept.`;
    },

    'file.audit': () => {
        const { entities } = useAppStore.getState();
        const ids = Object.keys(entities);
        const orphans = ids.filter((id) => !useDocumentStore.getState().sceneObjects[id]);
        const selfRefs = ids.filter((id) => {
            const e = entities[id];
            return e.type === 'line' && JSON.stringify(e.data.start) === JSON.stringify(e.data.end);
        });
        const issues = orphans.length + selfRefs.length;
        if (issues === 0) return `AUDIT: no errors found (${ids.length} entities checked).`;
        notify(i18n.t('tools.notify.auditResult', { orphans: orphans.length, selfRefs: selfRefs.length }), 'warning');
        return `AUDIT: ${issues} issue(s) reported (see messages).`;
    },

    'file.overkill': () => {
        const { entities, select, updateEntity, deleteEntity } = useAppStore.getState();
        const tol = 1e-4;
        const keys = Object.keys(entities);
        let removed = 0;
        for (let i = 0; i < keys.length; i++) {
            const a = entities[keys[i]];
            if (!a || a.type !== 'line') continue;
            for (let j = i + 1; j < keys.length; j++) {
                const b = entities[keys[j]];
                if (!b || b.type !== 'line') continue;
                const s1 = a.data.start as number[], e1 = a.data.end as number[];
                const s2 = b.data.start as number[], e2 = b.data.end as number[];
                if (!s1 || !e1 || !s2 || !e2) continue;
                const same = (Math.hypot(s2[0] - s1[0], s2[1] - s1[1], (s2[2] ?? 0) - (s1[2] ?? 0)) < tol
                    && Math.hypot(e2[0] - e1[0], e2[1] - e1[1], (e2[2] ?? 0) - (e1[2] ?? 0)) < tol)
                    || (Math.hypot(s2[0] - e1[0], s2[1] - e1[1], (s2[2] ?? 0) - (e1[2] ?? 0)) < tol
                        && Math.hypot(e2[0] - s1[0], e2[1] - s1[1], (e2[2] ?? 0) - (s1[2] ?? 0)) < tol);
                if (same) { deleteEntity(b.id); removed++; }
            }
        }
        select([]);
        void updateEntity;
        return removed > 0 ? `OVERKILL: ${removed} duplicate line(s) removed.` : 'OVERKILL: no duplicates found.';
    },

    // ---------------- underlays & point clouds (Faz 9.7 / 9.9) ----------------

    // PDFATTACH: place one PDF page as a scaled image entity. pdf.js is an
    // optional runtime peer (pdfRaster.ts) — without it a scaled frame is
    // placed and the user is told what installing pdfjs-dist adds.
    'file.pdfattach': () => {
        void (async () => {
            const file = await pickLocalFile('.pdf');
            if (!file) {
                notify(i18n.t('dialogs.ui.pdfAttachMissing'), 'warning');
                return;
            }
            const firstRaster = await renderPdfPage(file, 0);
            if (!firstRaster) {
                notify(i18n.t('dialogs.ui.pdfRasterUnavailable'), 'warning');
            }
            const pageCount = firstRaster?.pageCount ?? 1;
            runInteraction({
                steps: [
                    PN(`PDFATTACH: ${i18n.t('dialogs.ui.pdfAttachPickPage', { pages: pageCount })}`, 1),
                    PN(`PDFATTACH: ${i18n.t('dialogs.ui.pdfAttachScale')}`, 1),
                    PT('PDFATTACH: insertion point'),
                ],
                complete: (pts, values) => {
                    const pageNo = Math.max(1, Math.round(Number(values[0]) || 1));
                    const scale = Number(values[1]) || 1;
                    void (async () => {
                        const raster = await renderPdfPage(file, pageNo - 1);
                        const p = pts[0];
                        const base = raster ?? firstRaster;
                        undoableAdd([makeEntity('image', `PDF-${file.name}-p${pageNo}`, {
                            src: raster?.dataUrl ?? URL.createObjectURL(file),
                            srcKind: 'pdf',
                            fileName: file.name,
                            pageIndex: pageNo,
                            position: [p.x, p.y, p.z],
                            width: (base?.widthMeters ?? 8.5 * 0.0254) * scale,
                            height: (base?.heightMeters ?? 11 * 0.0254) * scale,
                            clip: null,
                            frame: true,
                        })]);
                    })();
                    return `PDFATTACH: placing page ${pageNo} of "${file.name}"`;
                },
            });
        })();
        return 'PDFATTACH: choose the PDF file…';
    },

    // PDFCLIP: rectangular or polygonal clip on a PDF/image underlay. OFF
    // removes the clip. Clip coordinates are stored normalized (0..1) so
    // the entity survives rescaling.
    'file.pdfclip': (args) => {
        const mode = (args?.[0] ?? '').toUpperCase();
        if (mode === 'OFF' || mode === 'ON') {
            runInteraction({
                steps: [PK('PDFCLIP: pick the underlay to unclip', ['image'])],
                complete: (_p, _v, picks) => {
                    const e = useAppStore.getState().entities[picks[0]];
                    if (!e) return 'PDFCLIP: underlay not found.';
                    undoableModify([e.id], (d) => ({ ...d, clip: null }));
                    notify(i18n.t('dialogs.ui.pdfClipRemoved', { name: e.name }), 'info');
                    return `PDFCLIP: clip removed from "${e.name}".`;
                },
            });
            return 'PDFCLIP: pick the underlay';
        }
        runInteraction({
            steps: [
                PK('PDFCLIP: pick the PDF underlay to clip', ['image']),
                PLoop(`PDFCLIP: ${i18n.t('dialogs.ui.pdfClipRect')} (POLYGON for 3+ points)`, 2),
            ],
            complete: (pts, _v, picks) => {
                const e = useAppStore.getState().entities[picks[0]];
                if (!e) return 'PDFCLIP: underlay not found.';
                const pos = ((e.data as Record<string, unknown>).position as number[]) ?? [0, 0, 0];
                const w = Math.max(Number((e.data as Record<string, unknown>).width) || 1, 1e-9);
                const h = Math.max(Number((e.data as Record<string, unknown>).height) || 1, 1e-9);
                const norm = pts.map((p): [number, number] => [
                    Math.min(Math.max((p.x - pos[0]) / w, 0), 1),
                    Math.min(Math.max((p.y - pos[1]) / h, 0), 1),
                ]);
                if (norm.length === 2) {
                    const clip = {
                        kind: 'rect' as const,
                        rect: [
                            Math.min(norm[0][0], norm[1][0]), Math.min(norm[0][1], norm[1][1]),
                            Math.max(norm[0][0], norm[1][0]), Math.max(norm[0][1], norm[1][1]),
                        ] as [number, number, number, number],
                    };
                    undoableModify([e.id], (d) => ({ ...d, clip }));
                    notify(i18n.t('dialogs.ui.pdfClipDone', { kind: 'rectangle', name: e.name }), 'success');
                    return 'PDFCLIP: rectangular clip applied.';
                }
                const clip = { kind: 'poly' as const, points: norm };
                undoableModify([e.id], (d) => ({ ...d, clip }));
                notify(i18n.t('dialogs.ui.pdfClipDone', { kind: 'polygon', name: e.name }), 'success');
                return `PDFCLIP: polygonal clip applied (${norm.length} corners).`;
            },
        });
        return `PDFCLIP: ${i18n.t('dialogs.ui.pdfClipPick')}`;
    },

    // FRAMES: global underlay frame visibility (0 off, 1 frame, 2 force all).
    'file.frames': (args) => {
        const raw = Number(args?.[0]);
        const value = Number.isFinite(raw) ? Math.max(0, Math.min(2, Math.round(raw))) : 1;
        useSystemVarsStore.getState().set('FRAMES', value);
        return i18n.t('dialogs.ui.pdfFramesSet', { value });
    },

    // PCATTACH (Faz 9.9): attach a point cloud into the 2D CAD ledger with
    // closed stride sampling (max 200k points). Display-only — no editing,
    // no osnap node (kapsam daraltması: tam pc_* paritesi hedeflenmez).
    'file.pcAttach': () => {
        void (async () => {
            const file = await pickLocalFile('.xyz,.pts,.asc,.ply,.csv,.txt');
            if (!file) return;
            try {
                const data = await parsePointCloudFile(file);
                const MAX_POINTS = 200_000;
                const stride = Math.max(1, Math.ceil(data.count / MAX_POINTS));
                const points: number[] = [];
                const colors: number[] = [];
                for (let i = 0; i < data.count; i += stride) {
                    points.push(data.positions[i * 3], data.positions[i * 3 + 1], data.positions[i * 3 + 2]);
                    colors.push(data.colors[i * 3], data.colors[i * 3 + 1], data.colors[i * 3 + 2]);
                }
                const shown = Math.ceil(data.count / stride);
                undoableAdd([makeEntity('pointcloud', file.name, {
                    points,
                    colors,
                    shown,
                    totalCount: data.count,
                    bounds: data.bounds,
                    size: 0.02,
                })]);
                notify(i18n.t('dialogs.ui.pointcloudParsed', { name: file.name, count: shown, total: data.count }), 'success');
            } catch (err) {
                notify(i18n.t('dialogs.ui.pointcloudFailed', { name: file.name, message: (err as Error).message }), 'error');
            }
        })();
        return 'PCATTACH: choose the point cloud file…';
    },

    // ---------------- alias editor (Faz 9.6) ----------------
    'tools.aliasEdit': () => {
        window.dispatchEvent(new CustomEvent('yap:open-alias-edit-dialog'));
        return 'ALIASEDIT: opening the alias editor.';
    },

    // ---------------- .scr script runner (Faz 10.7) ----------------
    // One command per line; blank = Enter; `;` comments; `delay <ms>` pauses.
    // Feeds the shared dispatcher exactly like typed input; Esc stops.
    'file.script': (args) => {
        const source = args?.join(' ').trim();
        if (source) {
            void (async () => {
                try {
                    const text = await loadScriptSource(source);
                    await runScriptSteps(parseScript(text), source.split(/[\\/]/).pop() || 'script.scr');
                } catch (e) {
                    useDynamicInputStore.getState().appendLog([
                        `SCRIPT: cannot load "${source}" — ${e instanceof Error ? e.message : String(e)}`, '',
                    ]);
                }
            })();
            return `SCRIPT: loading "${source}"…`;
        }
        void (async () => {
            if (isFileSystemAccessSupported()) {
                const handle = await pickFileToOpen();
                if (!handle) return;
                const text = await readHandleText(handle);
                await runScriptSteps(parseScript(text), handle.name);
                return;
            }
            // No File System Access: route through the in-app file browser.
            window.dispatchEvent(new CustomEvent('yap:open-file-browser', { detail: { mode: 'open' } }));
        })();
        return 'SCRIPT: choose the .scr file (one command per line; blank = Enter; ; comment; delay <ms>)…';
    },

    // ---------------- external reference operations (block-backed) ----------------
    // In this product the reference citizens are block INSERTs (the DWG
    // link itself lives in the BIM store). BIND freezes an insert against
    // definition changes by snapshotting the def into a private anonymous
    // block owned by that instance; XCLIP stores a real clip polygon the
    // BlockRefMesh renderer consumes; REFEDIT expands the reference for
    // in-place editing and BLOCK <name> redefines from the working set.
    'xref.bind': () => {
        runInteraction({
            steps: [PK('BIND: pick a block reference', ['insert'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'insert') return 'BIND: pick a block reference.';
                const name = String(e.data.blockName ?? '');
                const def = useBlockStore.getState().defs[name];
                if (!def) return 'BIND: block definition missing.';
                const boundName = `${name}$bound$${Date.now().toString(36)}`;
                useBlockStore.getState().define({
                    name: boundName,
                    entities: structuredClone(def.entities),
                    basePoint: [0, 0, 0],
                    attributes: structuredClone(def.attributes ?? []),
                });
                undoableModify([picks[0]], (d) => ({ ...d, blockName: boundName, bound: true }));
                return `BIND: "${name}" embedded as "${boundName}" — this instance no longer tracks the original definition.`;
            },
        });
        return 'BIND: pick a block reference';
    },
    'xref.clip': () => {
        runInteraction({
            steps: [PK('XCLIP: pick a reference (block/image)'), PLoopK('XCLIP: boundary corners (3+)', 3)],
            complete: (pts, _v, picks) => {
                const poly: Pt3[] = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                undoableModify([picks[0]], (d) => ({ ...d, clipBoundary: poly }));
                return 'XCLIP: clip boundary applied (contents outside the polygon hide).';
            },
        });
        return 'XCLIP: pick the reference';
    },
    'xref.edit': () => {
        const { selectedIds, entities } = useAppStore.getState();
        const id = selectedIds.find((i) => entities[i]?.type === 'insert');
        if (!id) return 'REFEDIT: select a block reference first.';
        const e = entities[id];
        const name = String(e.data.blockName ?? '');
        const def = useBlockStore.getState().defs[name];
        if (!def) return 'REFEDIT: block definition missing.';
        // Expand the reference into editable components tagged with its
        // source; BLOCK <name> afterwards redefines the block from a
        // selection that carries this working set (in-place cycle).
        const pos = (e.data.position as number[]) ?? [0, 0, 0];
        const scale = typeof e.data.scale === 'number' && e.data.scale > 0 ? e.data.scale : 1;
        const rot = ((e.data.rotation as number) ?? 0) * Math.PI / 180;
        const cos = Math.cos(rot), sin = Math.sin(rot);
        const mapPt = (pt: number[]): number[] => {
            const x = (pt[0] ?? 0) * scale, y = (pt[1] ?? 0) * scale;
            return [(pos[0] ?? 0) + x * cos - y * sin, (pos[1] ?? 0) + x * sin + y * cos, (pos[2] ?? 0) + (pt[2] ?? 0) * scale];
        };
        const working: Entity[] = def.entities.map((src) => {
            const data = structuredClone(src.data) as Record<string, any>;
            ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = mapPt(data[k]); });
            ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(mapPt); });
            return { ...src, id: `cad:${crypto.randomUUID()}`, data: { ...data, refEditSource: name } };
        });
        undoableReplace([id], working);
        useAppStore.getState().select(working.map((w) => w.id));
        notify(i18n.t('tools.notify.refeditExpanded', { name: name, blockName: name }), 'info');
        return 'REFEDIT: reference expanded for in-place editing.';
    },
    'xref.pointCloudCrop': () => {
        const bim = useBIMStore.getState();
        const cloudId = bim.selection.find((id) => (bim.project.elements.get(id) as any)?.category === 'Point Cloud')
            ?? [...bim.project.elements.values()].map((e: any) => (e.category === 'Point Cloud' ? e.id : null)).find(Boolean) as string | undefined;
        if (!cloudId) return 'POINTCLOUDCROP: no Point Cloud element in the project — attach a scan first.';
        runInteraction({
            steps: [PT('POINTCLOUDCROP: boundary corner 1'), PT('POINTCLOUDCROP: boundary corner 2')],
            complete: (pts) => {
                const cropMin: [number, number] = [Math.min(pts[0].x, pts[1].x), Math.min(pts[0].y, pts[1].y)];
                const cropMax: [number, number] = [Math.max(pts[0].x, pts[1].x), Math.max(pts[0].y, pts[1].y)];
                const el = useBIMStore.getState().project.elements.get(cloudId) as any;
                // LinkRenderer filters the vertex buffer by these parameters.
                useBIMStore.getState().updateElement(cloudId, {
                    parameters: { ...(el.parameters ?? {}), 'Crop Min': cropMin, 'Crop Max': cropMax },
                } as never);
                return 'POINTCLOUDCROP: scan cropped to the window (points outside hide).';
            },
        });
        return 'POINTCLOUDCROP: specify the crop window corners';
    },

    // ---------------- data & parameters ----------------
    'data.extract': () => {
        const { entities } = useAppStore.getState();
        const rows: Record<string, unknown>[] = Object.values(entities).map((e) => ({
            id: e.id, type: e.type, name: e.name, layer: e.layer, visible: e.visible,
            length: e.type === 'line' && e.data.start && e.data.end
                ? Number(Math.hypot(
                    (e.data.end[0] as number) - (e.data.start[0] as number),
                    (e.data.end[1] as number) - (e.data.start[1] as number)
                ).toFixed(4)) : null,
        }));
        const csv = ['id,type,name,layer,visible,length', ...rows.map((r) =>
            Object.values(r).map((v) => String(v ?? '')).join(',')
        )];
        downloadTextFile('data-extract.csv', csv.join('\n'), 'text/csv');
        return `DATAEXTRACTION: ${rows.length} rows written to data-extract.csv.`;
    },

    // TABLE: place a real in-drawing table entity. Rows/columns from a
    // CSV file when picked (via DATAEXTRACT-style file dialog) or a blank
    // grid sized by the prompts. The entity renders through EntityLayer.
    'data.table': (args) => {
        // A CSV file arg (DATALINK name or path) fills the grid; otherwise
        // ask for size + insertion point and create an empty table.
        const linkName = args?.[0]?.trim();
        const link = linkName ? useDataLinkStore.getState().get(linkName) : undefined;
        if (link && link.rows.length > 0) {
            runInteraction({
                steps: [PT('TABLE: insertion point')],
                complete: (pts) => {
                    const p = pts[0];
                    const cells = normalizeTableCells(link.rows);
                    undoableAdd([makeEntity('table', `Table-${Date.now()}`, {
                        cells, position: [p.x, p.y, p.z],
                        colWidth: 2, rowHeight: 0.6, hasHeader: true, linkName,
                    })]);
                    return `TABLE: inserted ${cells.length}x${cells[0].length} grid from link "${linkName}".`;
                },
            });
            return `TABLE: link "${linkName}" ready — specify insertion point`;
        }
        runInteraction({
            steps: [
                PN('TABLE: number of columns <3>'),
                PN('TABLE: number of rows (incl. header) <4>'),
                PT('TABLE: insertion point'),
            ],
            complete: (pts, values) => {
                const cols = Math.max(1, Math.round(Number(values[0]) || 3));
                const rows = Math.max(1, Math.round(Number(values[1]) || 4));
                const p = pts[0];
                const cells: string[][] = Array.from({ length: rows }, (_, r) =>
                    Array.from({ length: cols }, (_, c) => (r === 0 ? `Col ${c + 1}` : '')));
                undoableAdd([makeEntity('table', `Table-${Date.now()}`, {
                    cells, position: [p.x, p.y, p.z],
                    colWidth: 2, rowHeight: 0.6, hasHeader: true,
                })]);
                return `TABLE: ${rows}x${cols} table placed (double-click a cell to edit).`;
            },
        });
        return 'TABLE: specify columns';
    },

    'data.link': (args) => {
        // DATALINK (Faz 8.7): the dialog drives CSV/XLSX picking, sheet +
        // range selection, preview and binding (a table places at a point).
        // A name argument is still honored as the suggested link name.
        void args;
        useAppStore.getState().openManageDialog('dataLink');
        return 'DATALINK: Data Link dialog opened (Excel/CSV).';
    },

    // TABLEUPDATE (Faz 8.7): re-read linked tables from their datalink
    // (Refresh core) — one undoable modify per table.
    'data.tableUpdate': () => {
        const list = requireSelection('TABLEUPDATE');
        if (!list) return 'TABLEUPDATE: select tables first.';
        const tables = list.filter((e) => e.type === 'table' && typeof e.data.linkName === 'string');
        if (tables.length === 0) return 'TABLEUPDATE: no linked tables in the selection.';
        const { links } = useDataLinkStore.getState();
        let updated = 0;
        tables.forEach((e) => {
            const link = links[String(e.data.linkName)];
            if (!link || link.rows.length === 0) return;
            undoableModify([e.id], (d) => ({ ...d, cells: normalizeTableCells(structuredClone(link.rows)) }));
            updated++;
        });
        return updated > 0
            ? `TABLEUPDATE: ${updated} linked table(s) refreshed from their datalink.`
            : 'TABLEUPDATE: link data unavailable (re-pick the source file in DATALINK).';
    },

    'par.manager': (args) => {
        const action = (args?.[0] ?? 'list').toLowerCase();
        const mgr = globalParameterManager;
        if (action === 'list') {
            const all = mgr.getAll?.() ?? (mgr as unknown as { parameters?: Record<string, unknown> }).parameters;
            return all ? `PARAMETERS: ${Object.keys(all).join(', ') || '(none)'}` : 'PARAMETERS: (none defined)';
        }
        const [name = `Param${Date.now() % 1000}`, value = '0'] = args?.slice(1) ?? [];
        (mgr as unknown as { setParameter?: (n: string, v: unknown) => void }).setParameter?.(name, Number(value));
        return `PARAMETERS: "${name}" = ${value}.`;
    },
    'par.dim': (args) => {
        const list = requireSelection('DIMCONSTRAINT');
        if (!list) return 'DIMCONSTRAINT: select geometry first.';
        const name = args?.[0] ?? `Dim${Date.now() % 10000}`;
        const value = Number(args?.[1]) || 1;
        undoableModify(list.map((e) => e.id), (d) => ({ ...d, constraints: [...((d.constraints as string[]) ?? []), `${name}=${value}`] }));
        (globalParameterManager as unknown as { setParameter?: (n: string, v: unknown) => void }).setParameter?.(name, value);
        return `DIMCONSTRAINT: "${name}" = ${value} bound to ${list.length} object(s).`;
    },
    'par.geom': (args) => {
        const kind = (args?.[0] ?? 'coincident').toLowerCase();
        const list = requireSelection('GEOMCONSTRAINT');
        if (!list) return 'GEOMCONSTRAINT: select two objects first.';
        undoableModify(list.map((e) => e.id), (d) => ({ ...d, constraints: [...((d.constraints as string[]) ?? []), kind] }));
        return `GEOMCONSTRAINT: ${kind} applied to ${list.length} object(s).`;
    },

    // ---------------- automation / system ----------------
    'sys.script': () => {
        runInteraction({
            steps: [PTxt('SCRIPT: command lines separated by ";"')],
            complete: (_p, values) => {
                const lines = String(values[0] ?? '').split(';').map((s) => s.trim()).filter(Boolean);
                // Queue through the shared draft-command executor (lazy import
                // keeps the dynamic-input engine out of the boot graph), so
                // SCRIPT replays identically in every workbench — the old
                // 'yap:run-command' event only had an 2D-mode listener
                // and vanished into the void elsewhere.
                lines.forEach((line, i) => {
                    window.setTimeout(() => {
                        import('../../dynamicInput/dynamicInputEngine')
                            .then(({ executeDraftCommand }) => executeDraftCommand(line))
                            .catch(() => notify(i18n.t('tools.notify.scriptLineFailed', { line: line }), 'error'));
                    }, i * 50);
                });
                return `SCRIPT: ${lines.length} command(s) queued.`;
            },
        });
        return 'SCRIPT: enter commands';
    },
    'sys.macroRecord': (args) => {
        const macro = useMacroStore.getState();
        if ((args?.[0] ?? '').toUpperCase() === 'STOP' || (args?.[0] ?? '').toUpperCase() === 'ACTSTOP') {
            const steps = macro.stopAndName('macro');
            notify(i18n.t('tools.notify.actstopDone', { count: steps.length }), 'success');
            return `ACTSTOP: ${steps.length} steps captured.`;
        }
        if (macro.recording) return 'ACTRECORD: already recording (ACTSTOP to finish).';
        macro.start();
        notify(i18n.t('tools.notify.actrecordRecordingCommandsRunActstopTo'), 'info');
        return 'ACTRECORD: started. Use ACTSTOP to stop.';
    },
    'sys.historyToggle': () => {
        const s = useViewStore.getState();
        const next = !s.recordHistory;
        s.set('recordHistory', next);
        return `Record History: ${next ? 'ON — dependent geometry recomputes when its sources change.' : 'OFF — new command outputs will not record provenance.'}.`;
    },
    'sys.appLoad': () => {
        useAppStore.getState().openManageDialog('appLoad');
        return 'APPLOAD: Load Application dialog opened.';
    },
    'sys.reinit': () => {
        const pgp = useAcadPgpStore.getState();
        if (pgp.entries.length === 0 && pgp.extras.length === 0) {
            return 'REINIT: no imported acad.pgp to re-apply. Import one via ALIAS → Import acad.pgp.';
        }
        const summary = reapplyAcadPgp();
        return `REINIT: re-applied ${summary.presetAliases} alias(es) across ${summary.presetCommands} command(s) from ${pgp.fileName ?? 'acad.pgp'}.`;
    },
    'sys.standards': () => {
        const { layers } = useAppStore.getState();
        const bad = Object.values(layers).filter((l) => !l.color || !l.name);
        return bad.length === 0
            ? `CHECKSTANDARDS: ${Object.keys(layers).length} layer(s) conform.`
            : `CHECKSTANDARDS: ${bad.length} non-conforming layer(s) found.`;
    },
    'sys.compare': () => {
        downloadTextFile(`snapshot-${Date.now()}.json`, JSON.stringify(Object.values(useDocumentStore.getState().sceneObjects), null, 2));
        notify(i18n.t('tools.notify.dwgcompareCurrentSnapshotExportedCompareWith'), 'info');
        return 'DWGCOMPARE: snapshot exported for comparison.';
    },
    'sys.visualScripting': () => {
        notify(i18n.t('tools.notify.visualScriptingFrontEnd'), 'info');
        return 'VISUAL SCRIPTING: deferred by plan (docs/plans/3d/3d-full-parity-plan.md R7); the geometry kernel exists but the node editor does not.';
    },
};

/** Format import via the file picker: decode what we can, report honestly otherwise. */
function importFormat(label: string, ext: string): string {
    void (async () => {
        await pickAndRead(label, (name, text) => {
            if (ext === 'obj' && name.toLowerCase().endsWith('.obj')) {
                // Faz 10.11: OBJ is a real importer — Wavefront mesh becomes a
                // BIM 'Imported OBJ' element rendered in the 3D workbench.
                try {
                    const result = parseObjText(text);
                    useBIMStore.getState().addElement({
                        id: uuidv4(),
                        category: 'Generic Models',
                        type: 'Imported OBJ',
                        parameters: {
                            'Imported From': name,
                            'Layer': '0',
                            'Vertices': String(result.vertexCount),
                            'Faces': String(result.faceCount),
                        },
                        constraints: [],
                        geometryData: {
                            origin: [0, 0, 0],
                            obj: { parts: result.parts },
                        },
                    } as any);
                    notify(i18n.t('tools.notify.objImported', { name, vertices: result.vertexCount, faces: result.faceCount }), 'success');
                } catch (err) {
                    notify(i18n.t('tools.notify.objImportFailed', { message: (err as Error).message }), 'error');
                }
                return;
            }
            if (ext === 'dgn' && (text.includes('Dgn') || name.endsWith('.dgn'))) {
                // Minimal DGN element scan is not implemented locally; DXF is the supported 2D path.
                notify(i18n.t('tools.notify.dgnOffline', { label: label }), 'info');
                return;
            }
            if (ext === 'fbx' && name.endsWith('.fbx')) {
                notify(i18n.t('tools.notify.fbxOffline', { label: label }), 'warning');
                return;
            }
            if (ext === 'dwf' && name.endsWith('.dwf')) {
                notify(i18n.t('tools.notify.dwfOffline', { label: label }), 'info');
                return;
            }
            // .yap/.yate/JSON → route through the document opener for real.
            const parsed = parseDocument(text);
            if (parsed) {
                notify(i18n.t('tools.notify.docRecognized', { label: label, name: name }), 'info');
                return;
            }
            notify(i18n.t('tools.notify.docUnsupported', { label: label, name: name }), 'error');
        });
    })();
    return `${label}: choose the file in the dialog…`;
}
